/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Switch checkbox sagas
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import get from 'lodash/get';
import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import { requestSaga } from '@/main/sagas/requestSaga';
import { IPersonalAccount } from '@/main/types/personalAccountTypes';
import { ADD_CHECKBOX, API_HOSTNAME, DELETE_CHECKBOX } from '@modules/app/Api';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { storageRemoveCookie } from '@modules/storage/storageActions';
import { makeSelectCookies } from '@modules/storage/storageSelectors';
import { ECookieKey } from '@modules/storage/storageTypes';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';
import { mapTableNameToExpensesPageCookie } from '@pages/PageExpensesAnalyzer/types/pageExpensesAnalyzerMapTypes';

import {
  getBasketCountSuccessful,
  loadTableListSuccessful,
  setBasketValid,
  setTableBasketParamsAction,
  setTableMainCheckbox,
  switchCheckboxLoading,
  switchCheckboxSuccessful,
} from '../actions/tableActions';
import { LOAD_TABLE_META } from '../constants/actionConstants';
import { ITableBasketParams } from '../reducers/tableReducer';
import {
  makeSelectorBasketContext,
  makeSelectorTableBasketAllIn,
  makeSelectorTableCount,
  makeSelectorTableCurrentName,
  makeSelectorTableData,
  makeSelectorTableFooter,
  makeSelectorTableSearchAfterValues,
} from '../selectors/tableSelectors';
import { MapTableNameToBasketApiPath } from '../types/tableMapTypes';
import { ISwitchCheckboxPayload, TableNameType } from '../types/tableTypes';
import { loadTableMetaSagas } from './loadTableMetaSagas';

enum ECheckboxActions {
  add = 'add',
  delete = 'delete',
}

export interface ICheckboxesPayload {
  items: Array<{
    id: number;
    label: string;
  }>;
}


export function* switchTableFastActionCheckboxSaga({
  payload,
}: {
  payload: ISwitchCheckboxPayload;
}): SagaIterator {
  const tableName: TableNameType = yield select(makeSelectorTableCurrentName());
  // get table data
  const data = yield select(makeSelectorTableData());
  const basketAllIn = yield select(makeSelectorTableBasketAllIn());
  const count = yield select(makeSelectorTableCount());
  const tableFooter = yield select(makeSelectorTableFooter());
  const searchAfterValues = yield select(makeSelectorTableSearchAfterValues());
  const isAccountsListTable = tableName === TableNameType.account;

  if (isAccountsListTable) {
    yield put(storageRemoveCookie({ key: ECookieKey.BILL_PAGE }));
  }
  const newData = data.map((item: IPersonalAccount) => {
    let fastActionChecked;
    if (payload.items) {
      const checkedItem = payload.items.find(
        (checkedItem) => item.id === checkedItem.id,
      );
      fastActionChecked = checkedItem ? checkedItem.value : item.fastActionChecked;
    } else {
      fastActionChecked = !!payload.isAllChecked;
    }
    return {
      ...item,
      fastActionChecked,
    };
  });

  const fastActionAllIn = 
    !!payload.isAllChecked 
    || isAccountsListTable && count === newData.filter((item: IPersonalAccount) => item.fastActionChecked).length;

  yield put(
    loadTableListSuccessful({
      basketAllIn,
      fastActionAllIn,
      count,
      data: [].concat(newData),
      tableFooter,
      tableName,
      searchAfterValues,
    }),
  );
};

export function* switchTableCheckboxSaga({
  payload,
}: {
  payload: ISwitchCheckboxPayload;
}): SagaIterator {
  const context = yield select(makeSelectorBasketContext());
  const tableName: TableNameType = yield select(makeSelectorTableCurrentName());
  const isCampaignsImprovementsFeature =
      yield select(makeSelectFeatureEnabled(FeatureNameEnum.CAMPAIGNS_IMPROVEMENT));
  const isCampaingTable = [
      TableNameType.campaignInfoAccount,
      TableNameType.campaignInfoSubscriber,
  ].includes(tableName);
  const apiPath = MapTableNameToBasketApiPath[tableName];
  if (!apiPath) return;

  const cookies = yield select(makeSelectCookies());
  if (
    mapTableNameToExpensesPageCookie[tableName]?.includes(
      cookies.EXPENSE_ANALYZER_PAGE,
    )
  ) {
    yield put(storageRemoveCookie({ key: ECookieKey.EXPENSE_ANALYZER_PAGE }));
  }
  if (tableName === TableNameType.account) {
    yield put(storageRemoveCookie({ key: ECookieKey.BILL_PAGE }));
  }

  let type: ECheckboxActions = ECheckboxActions.add;

  const checkboxes: ICheckboxesPayload = { items: [] };
  if (payload.items) {
    payload.items.forEach((checkedItem) => {
      checkboxes.items.push({
        id: checkedItem.id,
        label: checkedItem.label,
      });
    });
  }

  const checkedItems = payload.items?.filter((item) => item.value) || [];
  // get table data
  const data = yield select(makeSelectorTableData());
  // update api url
  let url = API_HOSTNAME + apiPath;
  if (payload.isAllChecked || checkedItems.length) {
    url += ADD_CHECKBOX;
  } else {
    url += DELETE_CHECKBOX;
    type = ECheckboxActions.delete;
  }
  if (context && payload.id) {
    url += `?context=${context}&id=${payload.id}`;
  } else if (context) {
    url += `?context=${context}`;
  } else if (payload.id) {
    url += `?id=${payload.id}`;
  }
  // set checked props to data
  const newData = data.map((item: IPersonalAccount) => {
    let checked;
    if (payload.items) {
      const checkedItem = payload.items.find(
        (checkedItem) => item.id === checkedItem.id,
      );
      checked = checkedItem ? checkedItem.value : item.checked;
    } else {
      checked = !!payload.isAllChecked;
    }
    return {
      ...item,
      checked,
    };
  });

  const basketAllIn = yield select(makeSelectorTableBasketAllIn());
  const count = yield select(makeSelectorTableCount());
  const tableFooter = yield select(makeSelectorTableFooter());
  const searchAfterValues = yield select(makeSelectorTableSearchAfterValues());

  yield put(
    loadTableListSuccessful({
      basketAllIn,
      count,
      data: [].concat(newData),
      tableFooter,
      tableName,
      searchAfterValues,
    }),
  );

  yield put(switchCheckboxLoading());

  if (payload.isAllChecked !== undefined) {
    yield put(setTableMainCheckbox({ basketAllIn: payload.isAllChecked }));
  }

  try {
    let response;
    if (type === ECheckboxActions.delete) {
      response = yield call(
        requestSaga,
        url,
        'POST',
        checkboxes.items.length ? payload : {},
      );
    } else {
      response = yield call(
        requestSaga,
        url,
        'POST',
        payload.isAllChecked
          ? {
              items: [],
              params: payload.params || {},
            }
          : payload,
      );
    }

    // get table checkbox
    // We can read the state allIn without one more call (requestTableCheckboxState)
    const allIn = get(response, 'allIn', false);
    const valid = get(response, 'valid', true);
    const params: ITableBasketParams = get(response, 'params');

    if (payload.isAllChecked === undefined || payload.isAllChecked !== allIn) {
      yield put(setTableMainCheckbox({ basketAllIn: allIn }));
    }
    if (params) {
      yield put(setTableBasketParamsAction(params));
    }

    yield put(getBasketCountSuccessful(response.count ?? 0));
    yield put(setBasketValid(valid));

    // отключение перезапроса меты для МК
    if (!(isCampaignsImprovementsFeature && isCampaingTable)) {
        yield call(loadTableMetaSagas, {
            payload: {
                force: true,
                tableName: tableName,
                skipLoadList: true,
                waitFilterAndSort: false,
            },
            type: LOAD_TABLE_META,
        });
    }

    yield put(switchCheckboxSuccessful());
  } catch (e) {
    yield put(setTableMainCheckbox({ basketAllIn }));
    yield put(
      loadTableListSuccessful({
        basketAllIn,
        count,
        data: [].concat(data),
        tableFooter,
        tableName,
        searchAfterValues,
      }),
    );
  }
}
